/**
 * routes.ts
 * desc: configuration file for application routes
 * note: protected routes must include an "auth" object; the AppRoute type
 *       rejects an entry that has neither a component nor a redirect
 *       every page route also declares how it reads in the header breadcrumb and
 *       which route sits above it (`breadcrumb` — see getBreadcrumbs in router/utilities)
 */

/* istanbul ignore file */

import { lazy, type ComponentType } from 'react';
import appUrls from './settings/app-urls';
import type { AppRoute, PageProps } from './router/types';

// connect()-wrapped pages that are still JS infer their outer props from an
// untyped mapStateToProps, which never lines up with PageProps — cast at the
// boundary and switch back to `lazy` when the module converts
const lazyUntyped = (loader: () => Promise<{ default: unknown }>) =>
    lazy(loader as () => Promise<{ default: ComponentType<PageProps> }>);

const routes: { appRoutes: AppRoute[]; isolatedRoutes: AppRoute[] } = {
    appRoutes: [
        {
            path: appUrls.ROOT,
            redirect:  appUrls.HOME,
            title: 'common.document.title',
        },
        {
            auth: {
                permissions: [ 'feature-ai:view' ]
            },
            path: appUrls.AI,
            breadcrumb: { labelKey: 'breadcrumb.ai', parent: 'HOME' },
            title: 'ai.document.title',
            component: lazyUntyped(() => import('./modules/Ai')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'messages:read', 'messages:write' ]
            },
            path: appUrls.AI_LABELING,
            breadcrumb: { labelKey: 'nav.link.labeling', parent: 'AI' },
            title: 'aiLabeling.document.title',
            component: lazyUntyped(() => import('./modules/AiLabeling')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'messages:read', 'messages:write' ]
            },
            path: appUrls.AI_LABELING_UPLOAD,
            breadcrumb: { labelKey: 'breadcrumb.labelingUpload', parent: 'AI_LABELING' },
            title: 'aiLabeling.document.title',
            component: lazyUntyped(() => import('./modules/AiLabeling')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'models:read', 'models:write' ]
            },
            path: appUrls.AI_INTENT_MODELS,
            breadcrumb: { labelKey: 'nav.link.intentModels', parent: 'AI' },
            title: 'aiIntentModels.document.title',
            component: lazyUntyped(() => import('./modules/AiIntentModels')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'models:read', 'models:write' ]
            },
            path: appUrls.AI_INTENT_MODELS_COMPARE,
            breadcrumb: { labelKey: 'breadcrumb.compare', parent: 'AI_INTENT_MODELS_DETAIL_READ' },
            title: 'aiIntentModelsCompare.document.title',
            component: lazy(() => import('./modules/AiIntentModelsCompare')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'models:write' ]
            },
            path: appUrls.AI_INTENT_MODELS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.intentModelsCreate', parent: 'AI_INTENT_MODELS' },
            title: 'aiIntentModelsCreate.document.title',
            component: lazy(() => import('./modules/AiIntentModelsCreate')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'models:read', 'models:write' ]
            },
            path: appUrls.AI_INTENT_MODELS_DETAIL_READ,
            breadcrumb: { entity: 'intentModel', parent: 'AI_INTENT_MODELS' },
            title: 'aiIntentModelsDetail.document.title',
            component: lazy(() => import('./modules/AiIntentModelsDetail')),
        },
        {
            auth: {
                required: [ 'feature-ai:view' ],
                permissions: [ 'models:write' ]
            },
            path: appUrls.AI_INTENT_MODELS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'AI_INTENT_MODELS_DETAIL_READ' },
            title: 'aiIntentModelsDetail.document.title',
            component: lazy(() => import('./modules/AiIntentModelsDetail')),
        },
        {
            auth: {
                permissions: [ 'trips:read' ]
            },
            path: appUrls.ANALYTICS,
            breadcrumb: { labelKey: 'nav.link.analytics', parent: 'HOME' },
            component: lazy(() => import('./modules/Analytics')),
            title: 'analytics.document.title',
        },
        {
            auth: {
                permissions: [ 'trips:read', 'trips:write' ]
            },
            path: appUrls.ANALYTICS_TRIPS,
            breadcrumb: { labelKey: 'nav.link.trips', parent: 'HOME' },
            title: 'analyticsTrips.document.title',
            component: lazy(() => import('./modules/AnalyticsTrips')),
        },
        {
            auth: {
                permissions: [ 'trips:read', 'trips:write' ]
            },
            path: appUrls.ANALYTICS_TRIPS_DETAIL_READ,
            breadcrumb: { entity: 'trip', parent: 'ANALYTICS_TRIPS' },
            title: 'analyticsTripsDetail.document.title',
            component: lazy(() => import('./modules/AnalyticsTripsDetail')),
        },
        {
            auth: {
                permissions: [ 'feature-components:view' ]
            },
            path: appUrls.COMPONENTS,
            breadcrumb: { labelKey: 'breadcrumb.components', parent: 'HOME' },
            title: 'components.document.title',
            component: lazy(() => import('./modules/Components')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'sources:read', 'sources:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTIONS,
            breadcrumb: { labelKey: 'nav.link.connections', parent: 'COMPONENTS' },
            title: 'componentsConnections.document.title',
            component: lazy(() => import('./modules/ComponentsConnections')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'sources:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTIONS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.connectionsCreate', parent: 'COMPONENTS_CONNECTIONS' },
            title: 'componentsConnectionsCreate.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'sources:read', 'sources:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTIONS_DETAIL_READ,
            breadcrumb: { entity: 'connection', parent: 'COMPONENTS_CONNECTIONS' },
            title: 'componentsConnectionsDetail.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'sources:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTIONS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_CONNECTIONS_DETAIL_READ' },
            title: 'componentsConnectionsDetail.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'source-types:read', 'source-types:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTION_TYPES,
            breadcrumb: { labelKey: 'nav.link.connectionTypes', parent: 'COMPONENTS' },
            title: 'componentsConnectionTypes.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionTypes')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'source-types:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTION_TYPES_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.connectionTypesCreate', parent: 'COMPONENTS_CONNECTION_TYPES' },
            title: 'componentsConnectionTypesCreate.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionTypesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'source-types:read', 'source-types:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTION_TYPES_DETAIL_READ,
            breadcrumb: { entity: 'connectionType', parent: 'COMPONENTS_CONNECTION_TYPES' },
            title: 'componentsConnectionTypesDetail.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionTypesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'source-types:write' ]
            },
            path: appUrls.COMPONENTS_CONNECTION_TYPES_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_CONNECTION_TYPES_DETAIL_READ' },
            title: 'componentsConnectionTypesDetail.document.title',
            component: lazy(() => import('./modules/ComponentsConnectionTypesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'IN_DEVELOPMENT' ]
            },
            path: appUrls.COMPONENTS_ENTITIES,
            breadcrumb: { labelKey: 'breadcrumb.entities', parent: 'COMPONENTS' },
            title: 'componentsEntities.document.title',
            component: lazy(() => import('./modules/ComponentsEntities')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'intents:read', 'intents:write' ]
            },
            path: appUrls.COMPONENTS_INTENTS,
            breadcrumb: { labelKey: 'nav.link.intents', parent: 'COMPONENTS' },
            title: 'componentsIntents.document.title',
            component: lazy(() => import('./modules/ComponentsIntents')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'intents:write' ]
            },
            path: appUrls.COMPONENTS_INTENTS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.intentsCreate', parent: 'COMPONENTS_INTENTS' },
            title: 'componentsIntentsCreate.document.title',
            component: lazy(() => import('./modules/ComponentsIntentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'intents:read', 'intents:write' ]
            },
            path: appUrls.COMPONENTS_INTENTS_DETAIL_READ,
            breadcrumb: { entity: 'intent', parent: 'COMPONENTS_INTENTS' },
            title: 'componentsIntentsDetail.document.title',
            component: lazy(() => import('./modules/ComponentsIntentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'intents:write' ]
            },
            path: appUrls.COMPONENTS_INTENTS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_INTENTS_DETAIL_READ' },
            title: 'componentsIntentsDetail.document.title',
            component: lazy(() => import('./modules/ComponentsIntentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'review-queues:read', 'review-queues:write' ]
            },
            path: appUrls.COMPONENTS_REVIEW_QUEUES,
            breadcrumb: { labelKey: 'nav.link.reviewQueues', parent: 'COMPONENTS' },
            title: 'componentsReviewQueues.document.title',
            component: lazy(() => import('./modules/ComponentsReviewQueues')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'review-queues:write' ]
            },
            path: appUrls.COMPONENTS_REVIEW_QUEUES_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.reviewQueuesCreate', parent: 'COMPONENTS_REVIEW_QUEUES' },
            title: 'componentsReviewQueuesCreate.document.title',
            component: lazy(() => import('./modules/ComponentsReviewQueuesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'review-queues:read', 'review-queues:write' ]
            },
            path: appUrls.COMPONENTS_REVIEW_QUEUES_DETAIL_READ,
            breadcrumb: { entity: 'reviewQueue', parent: 'COMPONENTS_REVIEW_QUEUES' },
            title: 'componentsReviewQueuesDetail.document.title',
            component: lazy(() => import('./modules/ComponentsReviewQueuesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'review-queues:write' ]
            },
            path: appUrls.COMPONENTS_REVIEW_QUEUES_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_REVIEW_QUEUES_DETAIL_READ' },
            title: 'componentsReviewQueuesDetail.document.title',
            component: lazy(() => import('./modules/ComponentsReviewQueuesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES,
            breadcrumb: { labelKey: 'nav.link.stepFactories', parent: 'COMPONENTS' },
            title: 'componentsStepFactories.document.title',
            component: lazy(() => import('./modules/ComponentsStepFactories')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.stepFactoriesCreate', parent: 'COMPONENTS_STEP_FACTORIES' },
            title: 'components.stepFactoriesCreate.document.title',
            component: lazyUntyped(() => import('./modules/ComponentsStepFactoriesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_DETAIL_READ,
            breadcrumb: { entity: 'stepFactory', parent: 'COMPONENTS_STEP_FACTORIES' },
            title: 'components.stepFactoriesCreate.document.title',
            component: lazyUntyped(() => import('./modules/ComponentsStepFactoriesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_STEP_FACTORIES_DETAIL_READ' },
            title: 'components.stepFactoriesCreate.document.title',
            component: lazyUntyped(() => import('./modules/ComponentsStepFactoriesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_VERSION_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.stepFactoryVersionsCreate', parent: 'COMPONENTS_STEP_FACTORIES_DETAIL_READ' },
            title: 'components.stepFactoriesVersionCreate.document.title',
            component: lazy(() => import('./modules/ComponentsStepFactoriesVersionDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_VERSION_DETAIL_READ,
            breadcrumb: { entity: 'stepFactoryVersion', parent: 'COMPONENTS_STEP_FACTORIES_DETAIL_READ' },
            title: 'components.stepFactoriesVersionDetail.document.title',
            component: lazy(() => import('./modules/ComponentsStepFactoriesVersionDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'ROLE_DEVELOPER' ]
            },
            path: appUrls.COMPONENTS_STEP_FACTORIES_VERSION_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_STEP_FACTORIES_VERSION_DETAIL_READ' },
            title: 'components.stepFactoriesVersionDetail.document.title',
            component: lazy(() => import('./modules/ComponentsStepFactoriesVersionDetail')),
        },
        {
            auth: {
                permissions: [ 'feature-custom-data:view' ]
            },
            path: appUrls.CUSTOM_DATA,
            breadcrumb: { labelKey: 'breadcrumb.customData', parent: 'HOME' },
            title: 'customData.document.title',
            component: lazy(() => import('./modules/CustomData')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'constants:read', 'constants:write' ]
            },
            path: appUrls.CONSTANTS,
            breadcrumb: { labelKey: 'nav.link.constants', parent: 'CUSTOM_DATA' },
            title: 'constants.document.title',
            component: lazy(() => import('./modules/Constants')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'constants:read', 'constants:write' ]
            },
            path: appUrls.CONSTANTS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.constantsCreate', parent: 'CONSTANTS' },
            title: 'constantsDetail.document.title',
            component: lazy(() => import('./modules/ConstantsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'constants:read', 'constants:write' ]
            },
            path: appUrls.CONSTANTS_DETAIL_READ,
            breadcrumb: { entity: 'constant', parent: 'CONSTANTS' },
            title: 'constantsDetail.document.title',
            component: lazy(() => import('./modules/ConstantsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'constants:read', 'constants:write' ]
            },
            path: appUrls.CONSTANTS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'CONSTANTS_DETAIL_READ' },
            title: 'constantsDetail.document.title',
            component: lazy(() => import('./modules/ConstantsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'collections:read', 'collections:write' ]
            },
            path: appUrls.COLLECTIONS,
            breadcrumb: { labelKey: 'nav.link.collections', parent: 'CUSTOM_DATA' },
            title: 'collections.document.title',
            component: lazy(() => import('./modules/Collections')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'collections:read', 'collections:write' ]
            },
            path: appUrls.COLLECTIONS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.collectionsCreate', parent: 'COLLECTIONS' },
            title: 'collectionsDetail.document.title',
            component: lazy(() => import('./modules/CollectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'collections:read', 'collections:write' ]
            },
            path: appUrls.COLLECTIONS_DETAIL_READ,
            breadcrumb: { entity: 'collection', parent: 'COLLECTIONS' },
            title: 'collectionsDetail.document.title',
            component: lazy(() => import('./modules/CollectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'collections:read', 'collections:write' ]
            },
            path: appUrls.COLLECTIONS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COLLECTIONS_DETAIL_READ' },
            title: 'collectionsDetail.document.title',
            component: lazy(() => import('./modules/CollectionsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'client-files:read', 'client-files:write' ]
            },
            path: appUrls.FILE_STORAGE,
            breadcrumb: { labelKey: 'nav.link.fileStorage', parent: 'CUSTOM_DATA' },
            title: 'fileStorage.document.title',
            component: lazy(() => import('./modules/FileStorage')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'environments:read', 'environments:write' ]
            },
            path: appUrls.ENVIRONMENTS,
            breadcrumb: { labelKey: 'nav.link.environments', parent: 'HOME' },
            title: 'environments.document.title',
            component: lazy(() => import('./modules/Environments')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'environments:write' ]
            },
            path: appUrls.ENVIRONMENTS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.environmentsCreate', parent: 'ENVIRONMENTS' },
            title: 'environmentsDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/EnvironmentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'environments:read', 'environments:write' ]
            },
            path: appUrls.ENVIRONMENTS_DETAIL_READ,
            breadcrumb: { entity: 'environment', parent: 'ENVIRONMENTS' },
            title: 'environmentsDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/EnvironmentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'environments:write' ]
            },
            path: appUrls.ENVIRONMENTS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'ENVIRONMENTS_DETAIL_READ' },
            title: 'environmentsDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/EnvironmentsDetail')),
        },
        {
            auth: {
                required: [ 'feature-custom-data:view' ],
                permissions: [ 'environment-field-definitions:read', 'environment-field-definitions:write' ]
            },
            path: appUrls.ENVIRONMENT_SCHEMA,
            breadcrumb: { labelKey: 'breadcrumb.environmentSchema', parent: 'ENVIRONMENTS' },
            title: 'environmentSchema.document.title',
            component: lazy(() => import('./modules/EnvironmentSchema')),
        },
        {
            path: appUrls.HOME,
            breadcrumb: { labelKey: 'nav.link.home' },
            title: 'home.document.title',
            component: lazy(() => import('./modules/Home')),
        },
        {
            auth: {
                permissions: [ 'feature-discover:view' ]
            },
            path: appUrls.DISCOVER,
            breadcrumb: { labelKey: 'breadcrumb.discover', parent: 'HOME' },
            title: 'discover.document.title',
            component: lazy(() => import('./modules/Discover')),
        },
        {
            auth: {
                permissions: [ 'users:read', 'users:write' ]
            },
            path: appUrls.SETTINGS_GDPR,
            breadcrumb: { labelKey: 'breadcrumb.gdpr', parent: 'SETTINGS' },
            title: 'gdpr.document.title',
            component: lazy(() => import('./modules/GdprDetail')),
        },
        {
            auth: {
                permissions: [ 'paths:write' ]
            },
            path: appUrls.JOURNEYS,
            breadcrumb: { labelKey: 'nav.link.journeys', parent: 'HOME' },
            title: 'journeys.document.title',
            component: lazy(() => import('./modules/Journeys')),
        },
        {
            auth: {
                permissions: [ 'paths:read', 'paths:write' ]
            },
            path: appUrls.JOURNEYS_DETAIL_READ,
            breadcrumb: { entity: 'journey', parent: 'JOURNEYS' },
            title: 'journeysDetail.document.title',
            component: lazyUntyped(() => import('./modules/JourneysDetail')),
        },
        {
            auth: {
                permissions: [ 'paths:write' ]
            },
            path: appUrls.JOURNEYS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'JOURNEYS_DETAIL_READ' },
            title: 'journeysDetailEdit.document.title',
            component: lazyUntyped(() => import('./modules/JourneysDetail')),
        },
        {
            auth: {},
            path: appUrls.LABS,
            breadcrumb: { labelKey: 'nav.link.labs', parent: 'HOME' },
            title: 'labs.document.title',
            component: lazy(() => import('./modules/Labs')),
        },
        {
            auth: {
                permissions: [ 'review-queues:read', 'review-queues:write' ]
            },
            path: appUrls.REVIEWS,
            breadcrumb: { labelKey: 'nav.link.reviews', parent: 'HOME' },
            title: 'reviews.document.title',
            component: lazy(() => import('./modules/Reviews')),
        },
        {
            auth: {
                permissions: [ 'review-items:read', 'review-items:write' ]
            },
            path: appUrls.REVIEWS_DETAIL,
            breadcrumb: { entity: 'reviewQueue', parent: 'REVIEWS' },
            title: 'reviewsDetail.document.title',
            component: lazyUntyped(() => import('./modules/ReviewsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rules:read', 'rules:write' ]
            },
            path: appUrls.RULES,
            breadcrumb: { labelKey: 'nav.link.rules', parent: 'COMPONENTS' },
            title: 'rules.document.title',
            component: lazy(() => import('./modules/Rules')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rules:read', 'rules:write' ]
            },
            path: appUrls.RULES_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.rulesCreate', parent: 'RULES' },
            title: 'rulesDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/RulesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rules:read', 'rules:write' ]
            },
            path: appUrls.RULES_DETAIL_READ,
            breadcrumb: { entity: 'rule', parent: 'RULES' },
            title: 'rulesDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/RulesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rules:read', 'rules:write' ]
            },
            path: appUrls.RULES_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'RULES_DETAIL_READ' },
            title: 'rulesDetail.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/RulesDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rule-sets:read', 'rule-sets:write' ]
            },
            path: appUrls.RULE_SETS,
            breadcrumb: { labelKey: 'nav.link.ruleSets', parent: 'COMPONENTS' },
            title: 'ruleSets.document.title',
            component: lazy(() => import('./modules/RuleSets')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rule-sets:read', 'rule-sets:write' ]
            },
            path: appUrls.RULE_SETS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.ruleSetsCreate', parent: 'RULE_SETS' },
            title: 'rulesDetail.document.title',
            component: lazy(() => import('./modules/RuleSetsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rule-sets:read', 'rule-sets:write' ]
            },
            path: appUrls.RULE_SETS_DETAIL_READ,
            breadcrumb: { entity: 'ruleSet', parent: 'RULE_SETS' },
            title: 'rulesDetail.document.title',
            component: lazy(() => import('./modules/RuleSetsDetail')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'rule-sets:read', 'rule-sets:write' ]
            },
            path: appUrls.RULE_SETS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'RULE_SETS_DETAIL_READ' },
            title: 'rulesDetail.document.title',
            component: lazy(() => import('./modules/RuleSetsDetail')),
        },
        {
            auth: {
                permissions: [ 'rule-sets:read', 'rule-sets:write' ]
            },
            path: appUrls.RULE_SET_EVALUATIONS_DETAIL_READ,
            breadcrumb: { entity: 'ruleSetEvaluation', parent: 'RULE_SETS' },
            title: 'ruleSetEvaluationsDetail.document.title',
            component: lazy(() => import('./modules/RuleSetEvaluationsDetail')),
        },
        {
            auth: {
                permissions: [ 'orders:read', 'orders:write' ]
            },
            path: appUrls.ORDER_REVIEWS_LIST,
            breadcrumb: { entity: 'reviewQueue', parent: 'REVIEWS' },
            title: 'orderReviewsList.document.title',
            orderSchemaPicker: true,
            component: lazy(() => import('./modules/OrderReviewsList')),
        },
        {
            auth: {
                permissions: [ 'review-items:read', 'review-items:write' ]
            },
            path: appUrls.ORDER_REVIEWS_DETAIL,
            breadcrumb: { entity: 'orderReviewItem', parent: 'ORDER_REVIEWS_LIST' },
            title: 'orderReviewsDetail.document.title',
            component: lazy(() => import('./modules/OrderReviewsDetail')),
        },
        {
            auth: {
                permissions: [ 'orders:read', 'orders:write' ]
            },
            path: appUrls.ORDERS_DETAIL_READ,
            breadcrumb: { entity: 'order', parent: 'ORDERS' },
            title: 'orderReviewsDetail.document.title',
            component: lazy(() => import('./modules/OrderReviewsDetail')),
        },
        {
            auth: {
                permissions: [ 'orders:read', 'orders:write' ]
            },
            path: appUrls.ORDERS,
            breadcrumb: { labelKey: 'nav.link.orders', parent: 'HOME' },
            title: 'orders.document.title',
            component: lazy(() => import('./modules/Orders')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'order-schemas:read', 'order-schemas:write' ]
            },
            path: appUrls.COMPONENTS_ORDER_SCHEMAS,
            breadcrumb: { labelKey: 'nav.link.orderSchemas', parent: 'COMPONENTS' },
            title: 'componentsOrderSchemas.document.title',
            component: lazy(() => import('./modules/ComponentsOrderSchemas')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'order-schemas:write' ]
            },
            path: appUrls.COMPONENTS_ORDER_SCHEMAS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.orderSchemasCreate', parent: 'COMPONENTS_ORDER_SCHEMAS' },
            title: 'componentsOrderSchemasDetail.document.title',
            component: lazy(() => import('./modules/ComponentsOrderSchema')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'order-schemas:read', 'order-schemas:write' ]
            },
            path: appUrls.COMPONENTS_ORDER_SCHEMAS_DETAIL_READ,
            breadcrumb: { entity: 'orderSchema', parent: 'COMPONENTS_ORDER_SCHEMAS' },
            title: 'componentsOrderSchemasDetail.document.title',
            component: lazy(() => import('./modules/ComponentsOrderSchema')),
        },
        {
            auth: {
                required: [ 'feature-components:view' ],
                permissions: [ 'order-schemas:write' ]
            },
            path: appUrls.COMPONENTS_ORDER_SCHEMAS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'COMPONENTS_ORDER_SCHEMAS_DETAIL_READ' },
            title: 'componentsOrderSchemasDetail.document.title',
            component: lazy(() => import('./modules/ComponentsOrderSchema')),
        },
        {
            auth: {
                permissions: [ 'users:read', 'users:write' ]
            },
            path: appUrls.SETTINGS,
            breadcrumb: { labelKey: 'nav.link.settings', parent: 'HOME' },
            title: 'settings.document.title',
            component: lazy(() => import('./modules/Settings')),
        },
        {
            auth: {
                permissions: [ 'users:read', 'users:write' ]
            },
            path: appUrls.SETTINGS_USERS,
            breadcrumb: { labelKey: 'breadcrumb.users', parent: 'SETTINGS' },
            title: 'settings.usersList.document.title',
            component: lazy(() => import('./modules/SettingsUsers')),
        },
        {
            auth: {
                permissions: [ 'users:write' ]
            },
            path: appUrls.SETTINGS_USERS_CREATE,
            breadcrumb: { labelKey: 'breadcrumb.usersCreate', parent: 'SETTINGS_USERS' },
            title: 'settings.usersDetail.document.title',
            component: lazy(() => import('./modules/SettingsUsersDetail')),
        },
        {
            auth: {
                permissions: [ 'users:read', 'users:write' ]
            },
            path: appUrls.SETTINGS_USERS_DETAIL_READ,
            breadcrumb: { entity: 'user', parent: 'SETTINGS_USERS' },
            title: 'settings.usersDetail.document.title',
            component: lazy(() => import('./modules/SettingsUsersDetail')),
        },
        {
            auth: {
                permissions: [ 'users:write' ]
            },
            path: appUrls.SETTINGS_USERS_DETAIL_WRITE,
            breadcrumb: { labelKey: 'common.action.edit', parent: 'SETTINGS_USERS_DETAIL_READ' },
            title: 'settings.usersDetail.document.title',
            component: lazy(() => import('./modules/SettingsUsersDetail')),
        },
        {
            auth: {
                permissions: [ 'feature-testing:view' ]
            },
            path: appUrls.TESTING,
            breadcrumb: { labelKey: 'breadcrumb.testing', parent: 'HOME' },
            title: 'testing.landing.document.title',
            component: lazy(() => import('./modules/Testing')),
        },
        {
            auth: {
                required: [ 'feature-testing:view' ],
                permissions: [ 'testing:read', 'testing:write', 'testing:execute' ]
            },
            path: appUrls.TESTING_WEBHOOK,
            breadcrumb: { labelKey: 'nav.link.webhookTester', parent: 'TESTING' },
            title: 'testing.webhook.document.title',
            component: lazy(() => import('./modules/TestingWebhook')),
        },
        {
            auth: {
                required: [ 'feature-testing:view' ],
                permissions: [ 'testing:read', 'testing:write', 'testing:execute' ]
            },
            path: appUrls.TESTING_RULE_SET,
            breadcrumb: { labelKey: 'nav.link.ruleSetTester', parent: 'TESTING' },
            title: 'testing.ruleSet.document.title',
            component: lazy(() => import('./modules/TestingRuleSet')),
        },
        {
            default: true,
            title: 'error404.document.title',
            component: lazy(() => import('./modules/Error404')),
        },

    ],
    isolatedRoutes: [
        {
            path: appUrls.FRONT_PLUGIN,
            title: 'frontPlugin.document.title',
            component: lazy(() => import('./modules/FrontPlugin')),
        }
    ]
};

export default routes;
